import { ScrollArea, Table, Text } from '@radix-ui/themes';
import { CSVBall } from 'classes/csv-ball';
import { CommonTableHoC } from 'components/common/table';
import env from 'config';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useMemo } from 'react';
import { useCookiesStore } from 'stores/use-cookies-store';

interface IProps {
  data: CSVBall[];
  simple?: boolean;
}

export const PitchUploaderBallTable = (props: IProps) => {
  const app = useCookiesStore((s) => s.app);

  const showPitcherTeamColumn = useMemo(
    () => props.data.some((d) => !!d.details.team),
    [props.data]
  );

  const cols = useMemo(() => {
    /** release position modified by upload option */
    const avgRelease =
      env.enable.pitch_upload_avg_release &&
      app.pitch_upload_options.average_release === true;

    /** release position modified by upload option */
    const avgPitch =
      env.enable.pitch_upload_avg_chars &&
      app.pitch_upload_options.average_pitch !== undefined;

    const output: ITypedTableColumn<CSVBall>[] = [
      {
        label: 'common.name',
        key: 'name',
        formatFn: (m) => m.details.name,
        sortRowsFn: (a, b, dir: number) =>
          a.details.name.localeCompare(b.details.name) * dir,
      },
      {
        label: 'common.pitcher',
        key: 'pitcher',
        formatFn: (m) => m.details.pitcher,
        sortRowsFn: (a, b, dir: number) =>
          a.details.pitcher.localeCompare(b.details.pitcher) * dir,
      },
      {
        label: 'common.team',
        key: 'team',
        invisible: !showPitcherTeamColumn,
        formatFn: (m) => m.details.team,
        sortRowsFn: (a, b, dir: number) =>
          (a.details.team ?? '').localeCompare(b.details.team ?? '') * dir,
      },
      {
        label: 'common.pitch-type',
        key: 'type',
        formatFn: (m) => m.details.type,
        sortRowsFn: (a, b, dir: number) =>
          a.details.type.localeCompare(b.details.type) * dir,
      },
      {
        label: t('pd.release-side-short') + (avgRelease ? '*' : ''),
        labelTooltip: avgRelease ? 'Modified by upload option' : undefined,
        key: 'release_side',
        subLabel: 'ft',
        align: 'right',
        formatFn: (m) => m.traj?.px.toFixed(2) ?? '',
        sortRowsFn: (a, b, dir: number) =>
          ((a.traj?.px ?? 0) > (b.traj?.px ?? 0) ? -1 : 1) * dir,
      },
      {
        label: t('pd.release-height-short') + (avgRelease ? '*' : ''),
        labelTooltip: avgRelease ? 'Modified by upload option' : undefined,
        key: 'release_height',
        subLabel: 'ft',
        align: 'right',
        formatFn: (m) => m.traj?.pz.toFixed(2) ?? '',
        sortRowsFn: (a, b, dir: number) =>
          ((a.traj?.pz ?? 0) > (b.traj?.pz ?? 0) ? -1 : 1) * dir,
      },
      {
        label: 'common.speed',
        key: 'release_velocity',
        subLabel: 'mph',
        align: 'right',
        formatFn: (m) => {
          if (m.bs) {
            /** bs speed is in MPH */
            const speedMPH = BallHelper.getSafeSpeed(m.bs);
            return speedMPH?.toFixed(0) ?? t('common.na');
          }

          /** default */
          return t('common.na');
        },
        sortRowsFn: (a, b, dir: number) => {
          const aValue = a.bs ? (BallHelper.getSafeSpeed(a.bs) ?? 0) : 0;
          const bValue = b.bs ? (BallHelper.getSafeSpeed(b.bs) ?? 0) : 0;
          return (aValue > bValue ? -1 : 1) * dir;
        },
      },
      {
        label: 'common.spin' + (avgPitch ? '*' : ''),
        labelTooltip: avgPitch ? 'Modified by upload option' : undefined,
        key: 'release_spin',
        subLabel: 'rpm',
        align: 'right',
        formatFn: (m) => {
          if (m.bs) {
            const spin = BallHelper.getSafeNetSpin(m.bs);
            return spin?.toFixed(0) ?? t('common.na');
          }

          /** default */
          return t('common.na');
        },
        sortRowsFn: (a, b, dir: number) => {
          const aValue = a.bs ? (BallHelper.getSafeNetSpin(a.bs) ?? 0) : 0;
          const bValue = b.bs ? (BallHelper.getSafeNetSpin(b.bs) ?? 0) : 0;
          return (aValue > bValue ? -1 : 1) * dir;
        },
      },
      {
        label: 'common.seam-latitude',
        key: 'latitude_deg',
        subLabel: 'deg',
        align: 'right',
        formatFn: (m) => m.seams?.latitude_deg.toFixed(2) ?? t('common.na'),
        sortRowsFn: (a, b, dir: number) => {
          const safeA = a.seams?.latitude_deg ?? Infinity;
          const safeB = b.seams?.latitude_deg ?? Infinity;
          return (safeA > safeB ? -1 : 1) * dir;
        },
      },
      {
        label: 'common.seam-longitude',
        key: 'longitude_deg',
        subLabel: 'deg',
        align: 'right',
        formatFn: (m) => m.seams?.longitude_deg.toFixed(2) ?? t('common.na'),
        sortRowsFn: (a, b, dir: number) => {
          const safeA = a.seams?.longitude_deg ?? Infinity;
          const safeB = b.seams?.longitude_deg ?? Infinity;
          return (safeA > safeB ? -1 : 1) * dir;
        },
      },
      {
        label: 'common.v-break',
        subLabel: 'in',
        key: 'breaks_zInches',
        align: 'right',
        formatFn: (m) => m.breaks?.zInches.toFixed(1) ?? t('common.na'),
        sortRowsFn: (a, b, dir: number) => {
          const va = a.breaks?.zInches ?? Infinity;
          const vb = b.breaks?.zInches ?? Infinity;
          return (va > vb ? -1 : 1) * dir;
        },
      },
      {
        label: 'common.h-break',
        subLabel: 'in',
        key: 'breaks_xInches',
        align: 'right',
        formatFn: (m) => {
          const breaks = m.breaks;
          return breaks ? (-1 * breaks.xInches).toFixed(1) : t('common.na');
        },
        sortRowsFn: (a, b, dir: number) => {
          const va = a.breaks?.xInches ?? Infinity;
          const vb = b.breaks?.xInches ?? Infinity;
          return (va > vb ? -1 : 1) * dir;
        },
      },
    ];

    return output;
  }, [app.pitch_upload_options]);

  if (props.simple) {
    return (
      <ScrollArea style={{ maxHeight: '400px' }} scrollbars="both">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              {cols.map((col, iCol) => (
                <Table.ColumnHeaderCell key={iCol} align={col.align}>
                  {t(col.label)}
                  &nbsp;
                  {col.subLabel && (
                    <Text
                      size={RADIX.TEXT.SIZE.SM}
                      color={RADIX.COLOR.SECONDARY}
                    >
                      {col.subLabel}
                    </Text>
                  )}
                </Table.ColumnHeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {props.data.map((data, iData) => (
              <Table.Row key={iData}>
                {cols.map((col, iCol) => {
                  const value =
                    col.formatFn?.(data, iData) ?? (data as any)[col.key];

                  return (
                    <Table.Cell key={iCol} align={col.align}>
                      <Text title={value} truncate>
                        {value}
                      </Text>
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </ScrollArea>
    );
  }

  const pagination: ITablePageable = {
    paginationID: PaginationID.PitchUploaderBalls,
    total: props.data.length,
    enablePagination: true,
  };

  const sort: ITableSortable = {
    enableSort: true,
  };

  return (
    <CommonTableHoC
      dataTestID="PitchUploaderBalls"
      identifier="PitchUploaderBallsTable"
      displayColumns={cols}
      displayData={props.data}
      {...pagination}
      {...sort}
    />
  );
};
